import { Currencies } from '@constants/enums';
import { WAITING_FOR_DUE_DATE } from '@constants/states';
import { NewPaymentMethods } from '@efex-components/payables/PaymentModal/types';
import { PayableTypes } from '@efex-services/client/types';
import { IEnterprisePayment } from '@efex-services/payments/types';
import useBoolean from '@main-hooks/useBoolean';
import { fetchPayments } from '@pages/payables/store/payments';
import {
  selectPaymentsData,
  selectPaymentsPaginate,
  selectPaymentsState,
} from '@pages/payables/store/payments/selectors';
import {
  selectBusiness,
  selectProfileData,
} from '@pages/profile/store/selectors';
import { CURRENCY_IDS } from '@utils/formater';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/store';
import { fetchWallets } from '@/modules/wallets/store';
import { fetchExchange } from '@/modules/rate/exchange/store';
import { IPaymentsTableProps } from '../types';

const usePaymentTable = ({
  payableType,
  setIsLoadingPayments,
}: IPaymentsTableProps) => {
  const company = useSelector(selectBusiness);
  const [invoiceData, setInvoiceData] = useState<any>();
  const dispatch = useAppDispatch();
  const userProfile = useSelector(selectProfileData);
  const pagination = useSelector(selectPaymentsPaginate);
  const payments = useSelector(selectPaymentsData);
  const paymentsState = useSelector(selectPaymentsState);

  const navigate = useNavigate();

  const [payables, setPayables] = useState<IEnterprisePayment[]>();
  const [selectedPayable, setSelectedPayable] = useState<IEnterprisePayment>();
  const [currentPage, setCurrentPage] = useState(1);
  const [expanded, setExpanded] = useState<number | undefined>(undefined);
  const [payForward, setPayForward] = useState<IEnterprisePayment | null>(null);

  const [isDrawerOpened, { setFalse: hideDrawer, setTrue: showDrawer }] =
    useBoolean(false);
  const [
    paymentOutModal,
    { setFalse: hidePaymentOutModal, setTrue: showPaymentOutModal },
  ] = useBoolean(false);
  const [
    isPaymenyRejectModalOpen,
    { setFalse: hidePaymenyRejectModal, setTrue: showPaymenyRejectModal },
  ] = useBoolean(false);

  const refetchPayments = (page?: number) => {
    if (userProfile?.company.enterpriseId) {
      dispatch(
        fetchPayments({
          enterpriseId: Number(userProfile.company.enterpriseId),
          params: {
            page: page ?? currentPage,
            status: payableType === PayableTypes.PENDING ? '2' : undefined,
          },
        })
      );
    }
  };

  const handleChangePage = (page: number) => {
    if (page === currentPage) return;
    setCurrentPage(page);
    refetchPayments(page);
  };

  const handlePayout = (payable: IEnterprisePayment) => {
    if (
      CURRENCY_IDS[payable.currencyId] !== CURRENCY_IDS[payable.desCurrencyId]
    ) {
      dispatch(
        fetchExchange({
          currency: CURRENCY_IDS[payable.currencyId] as Currencies,
          desCurrency: CURRENCY_IDS[payable.desCurrencyId] as Currencies,
          enterpriseId: company?.enterpriseId ?? '0',
        })
      );
    }
    setSelectedPayable(payable);
    showPaymentOutModal();
  };

  const handleRejectPayable = (payable: IEnterprisePayment) => {
    setSelectedPayable(payable);
    showPaymenyRejectModal();
  };

  const showInvoice = (uuid: string, origin: 'payment' | 'payable') => {
    setInvoiceData({
      origin,
      identify: uuid,
    });
    showDrawer();
  };

  const reloadWallets = () => {
    const enterpriseId = userProfile?.company?.enterpriseId;
    if (enterpriseId) dispatch(fetchWallets(enterpriseId));
  };

  const renderRateValue = (rate: IEnterprisePayment['rate']) => {
    if (!rate) return '-';
    const fixedRate = Number(parseFloat(rate.value));
    if (fixedRate < 1) {
      return (1 / fixedRate).toFixed(2);
    }
    return fixedRate.toFixed(2);
  };

  const renderCompleteRateValue = (rate: IEnterprisePayment['rate']) => {
    if (!rate) return '-';
    const fixedRate = Number(parseFloat(rate.value));
    if (fixedRate < 1) {
      return (1 / fixedRate).toFixed(4);
    }
    return fixedRate.toFixed(4);
  };

  const isValidExecutionDate = (date?: string): boolean => {
    if (!date) return false;
    return new Date(date) > new Date();
  };

  const handleExpandClick = (id: number) => {
    setExpanded(expanded === id ? undefined : id);
  };

  /**
   * Retrieves the status name of a given payment.
   *
   * @param {IEnterprisePayment} payment - The payment object containing status and forward remaining information.
   * @returns {string} - The status name of the payment. If the forward remaining percentage is 0, returns 'Waiting_for_due_date'.
   */
  const getStatusName = (payment: IEnterprisePayment): string => {
    const { status, forwardRemainingInfo } = payment;
    if (
      forwardRemainingInfo &&
      forwardRemainingInfo.remainingPercentage === 0 &&
      status.name === PayableTypes.PENDING_TO_PROCESS_SCHEDULED_PAYMENT
    ) {
      return WAITING_FOR_DUE_DATE;
    }
    return status.name;
  };

  /**
   * Retrieves the schedule date for a given payment.
   *
   * @param {IEnterprisePayment} payment - The payment object containing payment details.
   * @returns {string} - The schedule date as a string. If the payment method is 'forward' and there are partial payments,
   *                     it returns the creation date of the last partial payment. Otherwise, it returns the due date.
   */
  const getScheduleDate = (payment: IEnterprisePayment): string => {
    if (payment.paymentMethodId === NewPaymentMethods.forward) {
      const { partialPayments } = payment || [];
      if (!partialPayments?.length) return '';
      return partialPayments && partialPayments.length > 0
        ? partialPayments[partialPayments.length - 1].createdAt.split(' ')[0]
        : '';
    }
    return payment.dueDate;
  };

  const navigateToTransferDetails = (payment: IEnterprisePayment) => {
    navigate(`/transfer-details/${payment.uuid}`);
  };

  useEffect(() => {
    reloadWallets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refetchPayments(1);
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payableType]);

  useEffect(() => {
    if (payments) {
      setPayables(payments);
    }
  }, [payments]);

  useEffect(() => {
    setIsLoadingPayments(paymentsState.loading);
  }, [paymentsState.loading, setIsLoadingPayments]);

  return {
    payables,
    selectedPayable,
    currentPage,
    expanded,
    payForward,
    isDrawerOpened,
    paymentOutModal,
    isPaymenyRejectModalOpen,
    paymentsState,
    userProfile,
    invoiceData,
    pagination,
    handleChangePage,
    handlePayout,
    handleRejectPayable,
    showInvoice,
    renderRateValue,
    renderCompleteRateValue,
    isValidExecutionDate,
    handleExpandClick,
    getStatusName,
    getScheduleDate,
    setPayForward,
    hidePaymentOutModal,
    hidePaymenyRejectModal,
    hideDrawer,
    refetchPayments,
    navigateToTransferDetails,
  };
};

export default usePaymentTable;
